import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { TrendingUp, Users, DollarSign, Copy, Check, Link as LinkIcon, MousePointer, BarChart3, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const AffiliateDashboardPage = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Generate full referral link
  const baseUrl = window.location.origin;
  const referralLink = dashboard?.affiliate_code 
    ? `${baseUrl}?ref=${dashboard.affiliate_code}` 
    : '';

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch affiliate dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyAffiliateCode = () => {
    navigator.clipboard.writeText(dashboard?.affiliate_code || user?.affiliate_code);
    setCopiedCode(true);
    toast.success("Affiliate code copied!");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedLink(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareOnWhatsApp = () => {
    const message = encodeURIComponent(
      `🌟 Buy premium 24K Gold & 999 Silver coins at the best prices!\n\n` +
      `Use my referral link to shop: ${referralLink}\n\n` +
      `✨ BIS Hallmarked\n🚚 Free Shipping\n💰 Live Market Prices\n\n` +
      `- AM GOLD`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareOnTelegram = () => {
    const message = encodeURIComponent(
      `Buy premium 24K Gold & 999 Silver coins at AM GOLD!\n\nUse my referral link: ${referralLink}`
    );
    window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-[#121212] w-1/3" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-[#121212]" />)}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="affiliate-dashboard">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Affiliate Program</p>
          <h1 className="text-4xl font-bold text-white">Your Dashboard</h1>
        </div>

        {/* Referral Link Card - PROMINENT */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass p-6 mb-8 border border-[#D4AF37]/30"
          data-testid="referral-link-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <LinkIcon className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Your Unique Referral Link</h2>
          </div>
          
          <div className="bg-[#0a0a0a] border border-[rgba(255,255,255,0.1)] p-4 mb-4">
            <p className="text-[#D4AF37] font-mono text-lg break-all" data-testid="referral-link">
              {referralLink}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={copyReferralLink}
              className="btn-gold flex items-center gap-2"
              data-testid="copy-link-btn"
            >
              {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedLink ? "Copied!" : "Copy Link"}
            </Button>
            
            <Button
              onClick={shareOnWhatsApp}
              className="bg-[#25D366] hover:bg-[#20BD5A] text-white flex items-center gap-2"
              data-testid="share-whatsapp-btn"
            >
              <Share2 className="w-4 h-4" />
              WhatsApp
            </Button>
            
            <Button
              onClick={shareOnTelegram}
              className="bg-[#0088cc] hover:bg-[#0077b5] text-white flex items-center gap-2"
              data-testid="share-telegram-btn"
            >
              <Share2 className="w-4 h-4" />
              Telegram
            </Button>
          </div>

          <p className="text-sm text-[#737373] mt-4">
            Share this link with your network. When someone makes a purchase using your link, you earn 2% commission on the base metal value!
          </p>
        </motion.div>

        {/* Affiliate Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass p-6 mb-8"
          data-testid="affiliate-code-card"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-[#A3A3A3] mb-1">Your Affiliate Code</p>
              <p className="text-3xl font-mono text-[#D4AF37]" data-testid="affiliate-code">
                {dashboard?.affiliate_code || user?.affiliate_code}
              </p>
            </div>
            <button
              onClick={copyAffiliateCode}
              className="btn-outline flex items-center gap-2"
              data-testid="copy-code-btn"
            >
              {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedCode ? "Copied!" : "Copy Code"}
            </button>
          </div>
          <p className="text-sm text-[#737373] mt-4">
            Customers can also enter this code manually at checkout.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-12" data-testid="affiliate-stats">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="stat-card"
          >
            <DollarSign className="w-6 h-6 text-[#D4AF37] mb-2" />
            <div className="stat-value text-2xl">₹{dashboard?.total_earnings?.toLocaleString() || 0}</div>
            <div className="stat-label text-xs">Total Earnings</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="stat-card"
          >
            <TrendingUp className="w-6 h-6 text-[#00E676] mb-2" />
            <div className="stat-value text-2xl text-[#00E676]">₹{dashboard?.pending_earnings?.toLocaleString() || 0}</div>
            <div className="stat-label text-xs">Pending Payout</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="stat-card"
          >
            <MousePointer className="w-6 h-6 text-[#2196F3] mb-2" />
            <div className="stat-value text-2xl text-[#2196F3]">{dashboard?.total_clicks || 0}</div>
            <div className="stat-label text-xs">Link Clicks</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="stat-card"
          >
            <Users className="w-6 h-6 text-[#D4AF37] mb-2" />
            <div className="stat-value text-2xl">{dashboard?.total_referrals || 0}</div>
            <div className="stat-label text-xs">Conversions</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="stat-card"
          >
            <BarChart3 className="w-6 h-6 text-[#9C27B0] mb-2" />
            <div className="stat-value text-2xl text-[#9C27B0]">{dashboard?.conversion_rate || 0}%</div>
            <div className="stat-label text-xs">Conversion Rate</div>
          </motion.div>
        </div>

        {/* Commission Info */}
        <div className="glass p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">How Commissions Work</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-[#0a0a0a] border border-[rgba(255,255,255,0.05)]">
              <p className="text-2xl font-light text-[#D4AF37] mb-2">2%</p>
              <p className="text-sm text-[#A3A3A3]">Commission Rate</p>
              <p className="text-xs text-[#737373] mt-2">Fixed 2% on base live metal rate</p>
            </div>
            <div className="p-4 bg-[#0a0a0a] border border-[rgba(255,255,255,0.05)]">
              <p className="text-2xl font-light text-[#D4AF37] mb-2">Instant</p>
              <p className="text-sm text-[#A3A3A3]">Credit</p>
              <p className="text-xs text-[#737373] mt-2">Commission credited on order confirmation</p>
            </div>
            <div className="p-4 bg-[#0a0a0a] border border-[rgba(255,255,255,0.05)]">
              <p className="text-2xl font-light text-[#D4AF37] mb-2">Monthly</p>
              <p className="text-sm text-[#A3A3A3]">Payout</p>
              <p className="text-xs text-[#737373] mt-2">Payouts processed every month</p>
            </div>
          </div>
        </div>

        {/* Recent Commissions */}
        <div className="dashboard-card" data-testid="recent-commissions">
          <h2 className="text-lg font-semibold text-white mb-6">Recent Commissions</h2>
          
          {dashboard?.recent_commissions?.length === 0 ? (
            <div className="empty-state py-12">
              <DollarSign className="empty-state-icon" />
              <p className="text-[#737373]">No commissions yet. Share your link to start earning!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.recent_commissions?.map((commission) => (
                    <tr key={commission.id}>
                      <td className="text-white font-mono">#{commission.order_id.slice(0, 8)}</td>
                      <td className="text-[#D4AF37]">₹{commission.amount?.toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${commission.status === 'paid' ? 'delivered' : 'pending'}`}>
                          {commission.status}
                        </span>
                      </td>
                      <td className="text-[#737373]">
                        {new Date(commission.created_at).toLocaleDateString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AffiliateDashboardPage;
